/**
 * Admin: Azure Functions Management
 *
 * Wraps the `scaleFunctionApp` endpoint:
 *   GET  -> read site/plan/instance state
 *   POST -> change instance count (1-100) and/or SKU
 *           (B1/B2/B3/P0v3/P1v3/P2v3/P3v3)
 *
 * See API_DOCUMENTATION.md §17.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Zap, AlertTriangle, ArrowDown, ArrowUp, DollarSign, Power, Server, Database } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { useAuth } from '../contexts/AuthContext';
import {
  getFunctionAppStatus,
  getInfrastructureStatus,
  scaleFunctionApp,
  scaleInfrastructure,
  saveLastRun,
  loadLastRun,
  normalizeSku,
  FUNCTION_SCALE_PROFILES,
  SCALE_FUNCTION_APP_SKUS,
  SCALE_FUNCTION_APP_SKU_SPECS,
  MAX_READER_REPLICAS,
  SQL_NIGHTLY_VCORES,
  SQL_IDLE_VCORES,
  type FunctionScaleProfileId,
  type ScaleFunctionAppResponse,
  type ScaleFunctionAppSku,
  type ScaleInfraTarget,
  type LastRunRecord,
  type InfrastructureStatusResponse,
  type ScaleInfrastructureResponse,
} from '../services/adminApi';
import {
  PRICING_META,
  nightlyRunCost,
  readerHourlyCost,
  sqlHourlyCost,
} from '../services/azurePricing';
import {
  Card,
  CardTitle,
  CardDescription,
  PrimaryButton,
  SecondaryButton,
  RunningIndicator,
  ConfirmDialog,
  Banner,
  StatGrid,
  Metric,
  RawJson,
  LastRunCard,
  Field,
  Label,
  Input,
  Select,
  ButtonRow,
  SampleTable,
  InlineLabel,
} from '../components/admin/AdminUI';

const ACTION = 'scaleFunctionApp';
const AUTO_REFRESH_MS = 30_000;
const MIN_INSTANCES = 1;
/** Hard cap accepted by the backend (1–100). Per-SKU caps are enforced separately. */
const MAX_INSTANCES_HARD = 100;

const HOURS_PER_MONTH = 730; // Azure's standard billing month

/**
 * The nightly run's peak configuration, from `scripts/nightly-run.sh`. Used as
 * the default for the cost model so the headline figure describes the run that
 * actually happens rather than an arbitrary shape.
 */
const NIGHTLY_PEAK = {
  minutes: 120,
  functionSku: 'P1v3' as ScaleFunctionAppSku,
  functionInstances: 10,
  sqlVCores: SQL_NIGHTLY_VCORES,
  readerReplicas: MAX_READER_REPLICAS,
};

const WEBIQ_PEAK = {
  minutes: 120,
  functionSku: 'P0v3' as ScaleFunctionAppSku,
  functionInstances: 10,
  sqlVCores: SQL_IDLE_VCORES,
  readerReplicas: 0,
};

/** What the platform costs between runs, and the floor the run is measured against. */
const NIGHTLY_BASELINE = {
  functionSku: 'P0v3' as ScaleFunctionAppSku,
  functionInstances: 1,
  sqlVCores: SQL_IDLE_VCORES,
};

/** Six extraction runs per week, averaged over a calendar year. */
const RUNS_PER_MONTH = 26;

function fmt(v: unknown): string {
  return typeof v === 'number' ? v.toLocaleString() : '—';
}

function fmtUsd(v: number): string {
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
}

function fmtUsd2(v: number): string {
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function stateTone(state: string | undefined): 'success' | 'warning' | 'error' | 'neutral' {
  if (!state) return 'neutral';
  const s = state.toLowerCase();
  if (s === 'running') return 'success';
  if (s === 'stopped') return 'error';
  return 'warning';
}

export const AdminFunctionsPage: React.FC = () => {
  const { userInfo } = useAuth();

  const [status, setStatus] = useState<ScaleFunctionAppResponse | null>(null);
  const [infrastructure, setInfrastructure] = useState<InfrastructureStatusResponse | null>(null);
  const [endpointMissing, setEndpointMissing] = useState(false);
  const [infraEndpointMissing, setInfraEndpointMissing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchedAt, setFetchedAt] = useState<Date | null>(null);

  const [target, setTarget] = useState<number>(1);
  const [targetTouched, setTargetTouched] = useState(false);
  const [targetSku, setTargetSku] = useState<ScaleFunctionAppSku>('B1');
  const [skuTouched, setSkuTouched] = useState(false);
  const [targetAlwaysOn, setTargetAlwaysOn] = useState<boolean>(true);
  const [alwaysOnTouched, setAlwaysOnTouched] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [scaling, setScaling] = useState(false);
  const [scaleStartedAt, setScaleStartedAt] = useState<number | null>(null);
  const [scaleResult, setScaleResult] = useState<ScaleFunctionAppResponse | null>(null);
  const [lastRun, setLastRun] = useState<LastRunRecord<ScaleFunctionAppResponse> | null>(null);

  // Cost model inputs. Seeded from the nightly run's real peak so the default
  // answers "what does a nightly run cost?" without any fiddling.
  const [costMinutes, setCostMinutes] = useState<number>(NIGHTLY_PEAK.minutes);
  const [costReaderReplicas, setCostReaderReplicas] = useState<number>(NIGHTLY_PEAK.readerReplicas);

  // Manual infrastructure scaling (SQL vCores, Reader replica bounds).
  const [infraScaling, setInfraScaling] = useState<ScaleInfraTarget | null>(null);
  const [infraResult, setInfraResult] = useState<ScaleInfrastructureResponse | null>(null);
  const [infraError, setInfraError] = useState<string | null>(null);
  const [infraConfirm, setInfraConfirm] = useState<
    { target: ScaleInfraTarget; label: string; detail: string; payload: Record<string, number> } | null
  >(null);

  useEffect(() => {
    setLastRun(loadLastRun<ScaleFunctionAppResponse>(ACTION));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, infra] = await Promise.all([
        getFunctionAppStatus(),
        getInfrastructureStatus(),
      ]);
      if (data == null) {
        setEndpointMissing(true);
        setStatus(null);
      } else {
        setEndpointMissing(false);
        setStatus(data);
        // Initialize the target field from the live capacity on first load.
        const cap = data.plan?.capacity;
        if (typeof cap === 'number' && cap >= MIN_INSTANCES && !targetTouched) {
          setTarget(cap);
        }
        // Initialize the target SKU from the live SKU on first load.
        const liveSku = normalizeSku(data.plan?.sku?.name);
        if (liveSku && !skuTouched) {
          setTargetSku(liveSku);
        }
        // Initialize AlwaysOn toggle from live state until the user touches it.
        if (typeof data.site?.alwaysOn === 'boolean' && !alwaysOnTouched) {
          setTargetAlwaysOn(data.site.alwaysOn);
        }
      }
      if (infra == null) {
        setInfraEndpointMissing(true);
        setInfrastructure(null);
      } else {
        setInfraEndpointMissing(false);
        setInfrastructure(infra);
      }
      setFetchedAt(new Date());
    } catch (e: any) {
      setError(e?.message ?? 'Failed to read infrastructure status');
    } finally {
      setLoading(false);
    }
  }, [targetTouched, skuTouched, alwaysOnTouched]);

  useEffect(() => { load(); }, [load]);

  // Light auto-refresh while the tab is focused. Skipped while a scale is in
  // flight to avoid UI flicker.
  useEffect(() => {
    const tick = () => { if (!document.hidden && !scaling) load(); };
    const id = window.setInterval(tick, AUTO_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [load, scaling]);

  const currentCapacity = status?.plan?.capacity ?? status?.site?.numberOfWorkers;
  const liveInstanceCount = status?.liveInstanceCount;
  const liveSkuName = status?.plan?.sku?.name ?? status?.plan?.sku?.tier;
  const liveSku = normalizeSku(status?.plan?.sku?.name);
  const liveSpec = liveSku ? SCALE_FUNCTION_APP_SKU_SPECS[liveSku] : null;
  const siteState = status?.site?.state;
  const liveAlwaysOn = status?.site?.alwaysOn;
  const liveFleetVCpu = liveSpec && typeof currentCapacity === 'number'
    ? liveSpec.vCpu * currentCapacity
    : null;
  const liveFleetMemoryGb = liveSpec && typeof currentCapacity === 'number'
    ? liveSpec.memoryGb * currentCapacity
    : null;
  const infraComponents = infrastructure?.components;
  const sql = infraComponents?.sqlDatabase;
  const functionPlan = infraComponents?.functionAppPlan;
  const readerApi = infraComponents?.readerApi;
  const nightlyJob = infraComponents?.nightlyJob;
  const webIqJob = infraComponents?.webIqJob;

  const targetSpec = SCALE_FUNCTION_APP_SKU_SPECS[targetSku];
  const targetMaxCapacity = targetSpec.maxCapacity;
  const targetFleetVCpu = targetSpec.vCpu * target;
  const targetFleetMemoryGb = targetSpec.memoryGb * target;

  // Live (current) cost = live SKU hourly cost × current capacity
  const liveHourlyCost =
    liveSpec && typeof currentCapacity === 'number'
      ? liveSpec.costPerHour * currentCapacity
      : null;
  const liveMonthlyCost =
    liveSpec && typeof currentCapacity === 'number'
      ? liveSpec.costPerMonth * currentCapacity
      : null;

  // Projected cost = target SKU hourly cost × target instances
  const targetHourlyCost = targetSpec.costPerHour * target;
  const targetMonthlyCost = targetSpec.costPerMonth * target;
  const webIqHourlyCost =
    SCALE_FUNCTION_APP_SKU_SPECS[WEBIQ_PEAK.functionSku].costPerHour *
      WEBIQ_PEAK.functionInstances +
    sqlHourlyCost(WEBIQ_PEAK.sqlVCores);
  const baselineHourlyCost =
    SCALE_FUNCTION_APP_SKU_SPECS[NIGHTLY_BASELINE.functionSku].costPerHour *
      NIGHTLY_BASELINE.functionInstances +
    sqlHourlyCost(NIGHTLY_BASELINE.sqlVCores);

  const targetValid =
    Number.isFinite(target) && target >= MIN_INSTANCES && target <= MAX_INSTANCES_HARD;
  const overSkuCap = target > targetMaxCapacity;

  const skuChanged = liveSku != null && targetSku !== liveSku;
  const capacityChanged = currentCapacity != null && target !== currentCapacity;
  const alwaysOnChanged =
    typeof liveAlwaysOn === 'boolean' && targetAlwaysOn !== liveAlwaysOn;
  const targetChanged = skuChanged || capacityChanged || alwaysOnChanged;

  const direction = useMemo(() => {
    if (currentCapacity == null) return null;
    if (target > currentCapacity) return 'up' as const;
    if (target < currentCapacity) return 'down' as const;
    return null;
  }, [target, currentCapacity]);

  const selectedProfileId = useMemo(() => {
    const profile = Object.values(FUNCTION_SCALE_PROFILES).find((candidate) =>
      candidate.sku === targetSku &&
      candidate.instances === target &&
      candidate.alwaysOn === targetAlwaysOn
    );
    return profile?.id ?? null;
  }, [targetSku, target, targetAlwaysOn]);

  const selectProfile = (profileId: FunctionScaleProfileId) => {
    const profile = FUNCTION_SCALE_PROFILES[profileId];
    setTargetSku(profile.sku);
    setTarget(profile.instances);
    setTargetAlwaysOn(profile.alwaysOn);
    setTargetTouched(true);
    setSkuTouched(true);
    setAlwaysOnTouched(true);
  };

  const handleScale = () => {
    if (!targetValid || !targetChanged || scaling) return;
    setConfirmOpen(true);
  };

  /**
   * Cost of holding the nightly peak for the selected duration.
   *
   * Reader replicas are an input rather than a constant because the Reader
   * only scales out under load: 10 replicas for the whole window is the
   * worst case, not the expected case, and presenting it as fixed would
   * overstate the bill.
   */
  const costModel = useMemo(
    () =>
      nightlyRunCost({
        minutes: costMinutes,
        functionSku: NIGHTLY_PEAK.functionSku,
        functionSkuCostPerHour: SCALE_FUNCTION_APP_SKU_SPECS[NIGHTLY_PEAK.functionSku].costPerHour,
        functionInstances: NIGHTLY_PEAK.functionInstances,
        sqlVCores: NIGHTLY_PEAK.sqlVCores,
        readerReplicas: costReaderReplicas,
        baselineFunctionSku: NIGHTLY_BASELINE.functionSku,
        baselineFunctionSkuCostPerHour: SCALE_FUNCTION_APP_SKU_SPECS[NIGHTLY_BASELINE.functionSku].costPerHour,
        baselineFunctionInstances: NIGHTLY_BASELINE.functionInstances,
        baselineSqlVCores: NIGHTLY_BASELINE.sqlVCores,
      }),
    [costMinutes, costReaderReplicas],
  );

  const requestInfraScale = (
    target: ScaleInfraTarget,
    label: string,
    detail: string,
    payload: Record<string, number>,
  ) => {
    if (infraScaling) return;
    setInfraConfirm({ target, label, detail, payload });
  };

  const confirmInfraScale = async () => {
    const pending = infraConfirm;
    if (!pending) return;
    setInfraConfirm(null);
    setInfraScaling(pending.target);
    setInfraError(null);
    setInfraResult(null);
    try {
      const data = await scaleInfrastructure({ target: pending.target, ...pending.payload });
      if (data == null) {
        setInfraError('This deployment does not expose POST /api/infrastructure yet.');
        return;
      }
      setInfraResult(data);
      if (data.components) {
        setInfrastructure(data);
        setFetchedAt(new Date());
      }
      if (data.scaleError) {
        setInfraError(data.scaleError.message || 'Scale request failed');
      }
    } catch (e: any) {
      setInfraError(e?.message ?? 'Scale request failed');
    } finally {
      setInfraScaling(null);
      // ARM applies both changes asynchronously, so re-read shortly after to
      // pick up the settled state rather than the in-flight one.
      window.setTimeout(() => { load(); }, 8000);
    }
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    setScaling(true);
    setError(null);
    setScaleResult(null);
    setScaleStartedAt(Date.now());
    const startedAt = Date.now();
    try {
      // Only send the fields that actually changed, so the backend can apply
      // a targeted update (and so the response's `request` echo is accurate).
      const payload: { instances?: number; sku?: ScaleFunctionAppSku; alwaysOn?: boolean } = {};
      if (capacityChanged) payload.instances = target;
      if (skuChanged) payload.sku = targetSku;
      if (alwaysOnChanged) payload.alwaysOn = targetAlwaysOn;
      // If the user clicked Apply with neither changed (shouldn't happen,
      // but guard anyway) fall back to sending both.
      if (!payload.instances && !payload.sku && payload.alwaysOn === undefined) {
        payload.instances = target;
        payload.sku = targetSku;
      }
      const { data } = await scaleFunctionApp(payload);
      setScaleResult(data);
      // The scale response is also a full status snapshot — refresh state.
      if (data.site || data.plan) {
        setStatus(data);
        setFetchedAt(new Date());
      }
      const ok = data.result?.success === true;
      const record: LastRunRecord<ScaleFunctionAppResponse> = {
        timestamp: new Date().toISOString(),
        triggeredBy: userInfo.userName || userInfo.userId,
        durationMs: Date.now() - startedAt,
        summary: {
          targetInstances: payload.instances,
          targetSku: payload.sku,
          targetAlwaysOn: payload.alwaysOn,
          previousCapacity: data.result?.previousCapacity,
          newCapacity: data.result?.newCapacity,
          previousSku: data.result?.previousSku,
          newSku: data.result?.newSku,
          previousAlwaysOn: data.result?.previousAlwaysOn,
          newAlwaysOn: data.result?.newAlwaysOn,
          success: ok,
          ...(data.scaleError ? { error: data.scaleError.message } : {}),
        },
        response: data,
      };
      saveLastRun(ACTION, record);
      setLastRun(record);
    } catch (e: any) {
      setError(e?.message ?? 'Scale request failed');
    } finally {
      setScaling(false);
      setScaleStartedAt(null);
    }
  };

  return (
    <AdminLayout
      currentPage="functions"
      pageTitle="Scalable infrastructure"
      pageDescription="Monitor Azure SQL, the Function App plan, Reader API, and nightly Container Apps job."
    >
      {endpointMissing && (
        <Banner tone="warning">
          <strong>scaleFunctionApp endpoint not available.</strong> The backend
          has not yet deployed <code>/api/scaleFunctionApp</code>. This page
          will light up automatically once it's shipped. See
          <code> API_DOCUMENTATION.md §17</code>.
        </Banner>
      )}
      {infraEndpointMissing && (
        <Banner tone="warning">
          <strong>infrastructure endpoint not available.</strong> The backend
          has not yet deployed <code>/api/infrastructure</code>.
        </Banner>
      )}

      {error && <Banner tone="error">{error}</Banner>}

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <CardTitle>Infrastructure status</CardTitle>
            <CardDescription>
              Read-only scalable resource snapshot from <code>GET /api/infrastructure</code>.
            </CardDescription>
          </div>
          <ButtonRow>
            <SecondaryButton onClick={load} disabled={loading || scaling}>
              <RefreshCw size={16} />
              Refresh
            </SecondaryButton>
          </ButtonRow>
        </div>
        <StatGrid>
          <Metric
            label="Azure SQL"
            value={sql?.status ?? sql?.error ?? '—'}
            hint={[sql?.edition, sql?.vCores != null ? `${sql.vCores} vCores` : null].filter(Boolean).join(' · ') || sql?.name}
            tone={sql?.error ? 'warning' : 'neutral'}
          />
          <Metric
            label="Function plan"
            value={functionPlan?.sku?.name ?? functionPlan?.error ?? liveSkuName ?? '—'}
            hint={`workers ${functionPlan?.workerCount ?? currentCapacity ?? '—'} · AlwaysOn ${
              functionPlan?.alwaysOn === true ? 'On' : functionPlan?.alwaysOn === false ? 'Off' : '—'
            }`}
            tone={functionPlan?.error ? 'warning' : 'neutral'}
          />
          <Metric
            label="Reader API"
            value={readerApi?.currentReplicas == null ? (readerApi?.error ?? '—') : `${readerApi.currentReplicas} replicas`}
            hint={`min ${readerApi?.minReplicas ?? '—'} · max ${readerApi?.maxReplicas ?? '—'} · ${readerApi?.runningStatus ?? readerApi?.provisioningState ?? 'status unknown'}`}
            tone={readerApi?.error ? 'warning' : 'neutral'}
          />
          <Metric
            label="Nightly job"
            value={nightlyJob?.lastExecution?.status ?? nightlyJob?.provisioningState ?? nightlyJob?.error ?? '—'}
            hint={[nightlyJob?.cronSchedule, nightlyJob?.cpu != null ? `${nightlyJob.cpu} CPU` : null, nightlyJob?.memory].filter(Boolean).join(' · ') || nightlyJob?.name}
            tone={nightlyJob?.error ? 'warning' : 'neutral'}
          />
          <Metric
            label="Split WebIQ job"
            value={webIqJob?.lastExecution?.status ?? webIqJob?.provisioningState ?? webIqJob?.error ?? '—'}
            hint={[webIqJob?.cronSchedule, webIqJob?.cpu != null ? `${webIqJob.cpu} CPU` : null, webIqJob?.memory, webIqJob?.statusSource === 'configuration' ? 'configured status' : null].filter(Boolean).join(' · ') || webIqJob?.name}
            tone={webIqJob?.error ? 'warning' : 'neutral'}
          />
        </StatGrid>
        {infrastructure?.error && (
          <Banner tone="warning" style={{ marginTop: 12 }}>
            {infrastructure.message || infrastructure.error}
          </Banner>
        )}
        {fetchedAt && (
          <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 8 }}>
            Last refreshed {fetchedAt.toLocaleTimeString()}
          </div>
        )}
      </Card>

      <Card>
        <CardTitle style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <DollarSign size={18} /> Nightly run cost
        </CardTitle>
        <CardDescription>
          Extraction cost while it holds the scaled configuration:{' '}
          <strong>Functions {NIGHTLY_PEAK.functionSku} × {NIGHTLY_PEAK.functionInstances}</strong>,{' '}
          <strong>SQL {NIGHTLY_PEAK.sqlVCores} vCores</strong>, and{' '}
          <strong>Reader × {costReaderReplicas}</strong>. Between runs the platform sits at{' '}
          {NIGHTLY_BASELINE.functionSku} × {NIGHTLY_BASELINE.functionInstances} and{' '}
          {NIGHTLY_BASELINE.sqlVCores} vCores with the Reader at zero, so the figure that is
          actually attributable to the run is the <strong>added</strong> column — the rest is paid
          for around the clock either way.
        </CardDescription>

        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', margin: '12px 0 16px' }}>
          <Field style={{ minWidth: 200 }}>
            <Label htmlFor="cost-minutes">Duration (minutes)</Label>
            <Input
              id="cost-minutes"
              type="number"
              min={1}
              max={240}
              value={costMinutes}
              onChange={(e) => setCostMinutes(Math.max(1, Math.min(240, Number(e.target.value) || 0)))}
            />
            <InlineLabel>Extraction cap 120 min; WebIQ cap 180 min; hard deadline 240 min.</InlineLabel>
          </Field>
          <Field style={{ minWidth: 200 }}>
            <Label htmlFor="cost-reader">Reader replicas</Label>
            <Input
              id="cost-reader"
              type="number"
              min={0}
              max={MAX_READER_REPLICAS}
              value={costReaderReplicas}
              onChange={(e) => setCostReaderReplicas(Math.max(0, Math.min(MAX_READER_REPLICAS, Number(e.target.value) || 0)))}
            />
            <InlineLabel>Scales out on demand, so {MAX_READER_REPLICAS} is the worst case.</InlineLabel>
          </Field>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '2px solid #e8e8e8' }}>
                <th style={{ padding: '8px 12px 8px 0' }}>Component</th>
                <th style={{ padding: '8px 12px' }}>While running</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Per hour</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>{costMinutes} min</th>
                <th style={{ padding: '8px 12px' }}>At rest</th>
                <th style={{ padding: '8px 0 8px 12px', textAlign: 'right' }}>Added</th>
              </tr>
            </thead>
            <tbody>
              {costModel.components.map((component) => (
                <tr key={component.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '8px 12px 8px 0', fontWeight: 500 }}>{component.label}</td>
                  <td style={{ padding: '8px 12px', color: '#595959' }}>{component.configuration}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>{fmtUsd(component.hourlyCost)}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>{fmtUsd(component.runCost)}</td>
                  <td style={{ padding: '8px 12px', color: '#8c8c8c' }}>{component.baselineConfiguration}</td>
                  <td style={{ padding: '8px 0 8px 12px', textAlign: 'right', fontWeight: 600 }}>
                    {fmtUsd(component.incrementalRunCost)}
                  </td>
                </tr>
              ))}
              <tr style={{ borderTop: '2px solid #e8e8e8' }}>
                <td style={{ padding: '10px 12px 10px 0', fontWeight: 700 }}>Total</td>
                <td style={{ padding: '10px 12px' }} />
                <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700 }}>
                  {fmtUsd(costModel.totalHourlyCost)}
                </td>
                <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700 }}>
                  {fmtUsd(costModel.totalRunCost)}
                </td>
                <td style={{ padding: '10px 12px' }} />
                <td style={{ padding: '10px 0 10px 12px', textAlign: 'right', fontWeight: 700 }}>
                  {fmtUsd(costModel.incrementalRunCost)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <StatGrid style={{ marginTop: 16 }}>
          <Metric
            label={`Cost of one ${costMinutes}-minute run`}
            value={fmtUsd2(costModel.totalRunCost)}
            hint="everything running, including the at-rest platform"
          />
          <Metric
            label="Added by the run"
            value={fmtUsd2(costModel.incrementalRunCost)}
            hint="above what the idle platform costs anyway"
          />
          <Metric
            label="Added per month"
            value={fmtUsd2(costModel.incrementalRunCost * RUNS_PER_MONTH)}
            hint={`${RUNS_PER_MONTH} runs — one every night`}
          />
          <Metric
            label="Idle platform per month"
            value={fmtUsd2(
              (SCALE_FUNCTION_APP_SKU_SPECS[NIGHTLY_BASELINE.functionSku].costPerHour *
                NIGHTLY_BASELINE.functionInstances +
                sqlHourlyCost(NIGHTLY_BASELINE.sqlVCores)) *
                HOURS_PER_MONTH,
            )}
            hint="Functions and SQL at rest, 730 hr/mo"
          />
        </StatGrid>

        <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 12, lineHeight: 1.6 }}>
          Indicative only. List prices from the {PRICING_META.source} for{' '}
          {PRICING_META.regionLabel} in {PRICING_META.currency}, captured {PRICING_META.capturedOn}:
          SQL General Purpose Gen5 at $0.17/vCore-hour, Container Apps consumption at
          $0.000024/vCPU-second plus $0.000003/GiB-second, and the published App Service Premium v3
          rates. Reservations, savings plans and any enterprise discount are excluded, as is the
          monthly Container Apps free grant. A real run does not hold peak for its whole duration —
          the extraction preflight runs at baseline capacity first — so treat this as the ceiling
          and read <code>/admin/nightly-runs</code> for what a specific run actually consumed.
          Azure Cost Management remains the source of truth.
        </div>
        <StatGrid style={{ marginTop: 16 }}>
          <Metric
            label="WebIQ profile"
            value={`${WEBIQ_PEAK.functionSku} x${WEBIQ_PEAK.functionInstances}`}
            hint={`SQL ${WEBIQ_PEAK.sqlVCores} vCores · Reader off`}
          />
          <Metric
            label="WebIQ per hour"
            value={fmtUsd2(webIqHourlyCost)}
            hint={`${fmtUsd2(Math.max(0, webIqHourlyCost - baselineHourlyCost))}/hr above idle`}
          />
          <Metric
            label="WebIQ phase cap"
            value="180 min"
            hint="stops earlier on provider daily quota"
          />
        </StatGrid>
      </Card>

      <Card>
        <CardTitle style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Power size={18} /> Manual scaling — SQL and Reader
        </CardTitle>
        <CardDescription>
          The nightly run scales these up and back down on its own. These controls exist for when
          it cannot: if a run dies between scaling up and scaling down, the platform stays
          expensive until the next night unless someone intervenes. Changes are applied through{' '}
          <code>POST /api/infrastructure</code> and take a few minutes to settle.
        </CardDescription>

        {infraError && (
          <Banner tone="error" style={{ marginTop: 12 }}>
            {infraError}
          </Banner>
        )}
        {infraResult?.result?.success && !infraError && (
          <Banner tone="success" style={{ marginTop: 12 }}>
            {infraResult.result.changed === false
              ? 'Already at the requested configuration; nothing to change.'
              : 'Change accepted. Azure is applying it now — the status above will catch up within a few minutes.'}
          </Banner>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginTop: 16 }}>
          <Card style={{ background: '#fafafa', margin: 0 }}>
            <CardTitle style={{ fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Database size={16} /> Azure SQL
            </CardTitle>
            <CardDescription>
              Currently <strong>{sql?.vCores ?? '—'} vCores</strong>
              {sql?.vCores != null && ` · ${fmtUsd(sqlHourlyCost(sql.vCores))}/hr`}
              {sql?.status ? ` · ${sql.status}` : ''}
            </CardDescription>
            <ButtonRow style={{ marginTop: 12 }}>
              <PrimaryButton
                onClick={() =>
                  requestInfraScale(
                    'sql',
                    `Scale SQL up to ${SQL_NIGHTLY_VCORES} vCores`,
                    `${fmtUsd(sqlHourlyCost(SQL_NIGHTLY_VCORES))}/hr while it stays there, up from ${fmtUsd(sqlHourlyCost(sql?.vCores ?? SQL_IDLE_VCORES))}/hr.`,
                    { vCores: SQL_NIGHTLY_VCORES },
                  )
                }
                disabled={infraScaling != null || sql?.vCores === SQL_NIGHTLY_VCORES}
              >
                <ArrowUp size={16} /> Scale up to {SQL_NIGHTLY_VCORES} vCores
              </PrimaryButton>
              <SecondaryButton
                onClick={() =>
                  requestInfraScale(
                    'sql',
                    `Scale SQL down to ${SQL_IDLE_VCORES} vCores`,
                    `Returns to the idle configuration at ${fmtUsd(sqlHourlyCost(SQL_IDLE_VCORES))}/hr. Do not do this while a nightly run is draining.`,
                    { vCores: SQL_IDLE_VCORES },
                  )
                }
                disabled={infraScaling != null || sql?.vCores === SQL_IDLE_VCORES}
              >
                <ArrowDown size={16} /> Scale down to {SQL_IDLE_VCORES} vCores
              </SecondaryButton>
            </ButtonRow>
            {infraScaling === 'sql' && (
              <RunningIndicator startedAt={Date.now()} message="Applying SQL change…" />
            )}
          </Card>

          <Card style={{ background: '#fafafa', margin: 0 }}>
            <CardTitle style={{ fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Server size={16} /> Reader API
            </CardTitle>
            <CardDescription>
              Currently <strong>{readerApi?.currentReplicas ?? '—'} running</strong>, bounds{' '}
              min {readerApi?.minReplicas ?? '—'} / max {readerApi?.maxReplicas ?? '—'}
              {' · '}
              {fmtUsd(readerHourlyCost(1))}/hr per replica
            </CardDescription>
            <ButtonRow style={{ marginTop: 12 }}>
              <PrimaryButton
                onClick={() =>
                  requestInfraScale(
                    'reader',
                    'Keep one Reader replica warm',
                    `Sets min 1 / max ${MAX_READER_REPLICAS}. Removes cold-start before a manual heavy run, and costs ${fmtUsd(readerHourlyCost(1))}/hr continuously until you scale it back down.`,
                    { minReplicas: 1, maxReplicas: MAX_READER_REPLICAS },
                  )
                }
                disabled={infraScaling != null || (readerApi?.minReplicas === 1 && readerApi?.maxReplicas === MAX_READER_REPLICAS)}
              >
                <ArrowUp size={16} /> Keep warm (min 1)
              </PrimaryButton>
              <SecondaryButton
                onClick={() =>
                  requestInfraScale(
                    'reader',
                    'Allow the Reader to scale to zero',
                    `Sets min 0 / max ${MAX_READER_REPLICAS}. Running replicas drain after the five-minute cooldown and the Reader then costs nothing while idle.`,
                    { minReplicas: 0, maxReplicas: MAX_READER_REPLICAS },
                  )
                }
                disabled={infraScaling != null || (readerApi?.minReplicas === 0 && readerApi?.maxReplicas === MAX_READER_REPLICAS)}
              >
                <ArrowDown size={16} /> Scale to zero (min 0)
              </SecondaryButton>
            </ButtonRow>
            {infraScaling === 'reader' && (
              <RunningIndicator startedAt={Date.now()} message="Applying Reader change…" />
            )}
          </Card>
        </div>
      </Card>

      <ConfirmDialog
        open={infraConfirm != null}
        title={infraConfirm?.label ?? ''}
        body={<p style={{ margin: 0 }}>{infraConfirm?.detail}</p>}
        confirmPhrase="SCALE"
        primaryLabel={infraConfirm?.label ?? 'Apply'}
        isProcessing={infraScaling != null}
        onCancel={() => setInfraConfirm(null)}
        onConfirm={confirmInfraScale}
      />

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <CardTitle>Function App status</CardTitle>
            <CardDescription>
              Live state from <code>GET /api/scaleFunctionApp</code>. Auto-refreshes every 30s.
            </CardDescription>
          </div>
          <ButtonRow>
            <SecondaryButton onClick={load} disabled={loading || scaling}>
              <RefreshCw size={16} />
              Refresh
            </SecondaryButton>
          </ButtonRow>
        </div>

        <StatGrid>
          <Metric
            label="Site state"
            value={siteState ?? '—'}
            tone={stateTone(siteState)}
          />
          <Metric
            label="Current capacity (plan)"
            value={fmt(currentCapacity)}
            hint="Configured worker count"
          />
          <Metric
            label="Live instances"
            value={fmt(liveInstanceCount)}
            hint="Workers currently online"
            tone={
              currentCapacity != null && liveInstanceCount != null && liveInstanceCount < currentCapacity
                ? 'warning'
                : 'neutral'
            }
          />
          <Metric
            label="SKU"
            value={liveSkuName ?? '—'}
            hint={status?.plan?.sku?.tier}
          />
          <Metric
            label="Fleet vCPU"
            value={liveFleetVCpu != null ? liveFleetVCpu : '—'}
            hint={liveSpec && currentCapacity != null ? `${liveSpec.vCpu} × ${currentCapacity} workers` : undefined}
          />
          <Metric
            label="Fleet memory"
            value={liveFleetMemoryGb != null ? `${liveFleetMemoryGb} GB` : '—'}
            hint={liveSpec && currentCapacity != null ? `${liveSpec.memoryGb} GB × ${currentCapacity} workers` : undefined}
          />
          <Metric label="Region" value={status?.site?.location ?? status?.plan?.location ?? '—'} />
          <Metric
            label="Cost / hour"
            value={liveHourlyCost != null ? fmtUsd(liveHourlyCost) : '—'}
            hint={
              liveSpec && typeof currentCapacity === 'number'
                ? `${fmtUsd(liveSpec.costPerHour)} × ${currentCapacity} instance${currentCapacity === 1 ? '' : 's'}`
                : 'Live SKU × current capacity'
            }
          />
          <Metric
            label="Cost / month"
            value={liveMonthlyCost != null ? fmtUsd2(liveMonthlyCost) : '—'}
            hint={
              liveSpec && typeof currentCapacity === 'number'
                ? `${fmtUsd2(liveSpec.costPerMonth)} × ${currentCapacity} instance${currentCapacity === 1 ? '' : 's'} (730 hr/mo)`
                : 'Indicative — public retail prices'
            }
          />
          <Metric
            label="HTTPS only"
            value={status?.site?.httpsOnly ? 'Yes' : status?.site ? 'No' : '—'}
            tone={status?.site?.httpsOnly === false ? 'warning' : 'neutral'}
          />
          <Metric
            label="Always On"
            value={
              liveAlwaysOn === true ? 'On' :
              liveAlwaysOn === false ? 'Off' : '—'
            }
            tone={
              liveAlwaysOn === true ? 'success' :
              liveAlwaysOn === false ? 'warning' : 'neutral'
            }
            hint="Kept enabled by both recommended profiles"
          />
        </StatGrid>

        {fetchedAt && (
          <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 8 }}>
            Last refreshed {fetchedAt.toLocaleTimeString()}
          </div>
        )}

        {liveAlwaysOn === false && !endpointMissing && (
          <Banner tone="warning" style={{ marginTop: 12 }}>
            <Power size={14} style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />
            <strong>Always On is disabled.</strong> The nightly governance
            policy likely turned it off. Click <em>Re-enable Always On</em> below
            to flip it back on without changing SKU or capacity.
            <ButtonRow>
              <PrimaryButton
                onClick={async () => {
                  setScaling(true);
                  setError(null);
                  setScaleResult(null);
                  setScaleStartedAt(Date.now());
                  const startedAt = Date.now();
                  try {
                    const { data } = await scaleFunctionApp({ alwaysOn: true });
                    setScaleResult(data);
                    if (data.site || data.plan) {
                      setStatus(data);
                      setFetchedAt(new Date());
                      if (typeof data.site?.alwaysOn === 'boolean') {
                        setTargetAlwaysOn(data.site.alwaysOn);
                      }
                    }
                    const ok = data.result?.success === true;
                    const record: LastRunRecord<ScaleFunctionAppResponse> = {
                      timestamp: new Date().toISOString(),
                      triggeredBy: userInfo.userName || userInfo.userId,
                      durationMs: Date.now() - startedAt,
                      summary: {
                        action: 'enable-alwaysOn',
                        previousAlwaysOn: data.result?.previousAlwaysOn,
                        newAlwaysOn: data.result?.newAlwaysOn,
                        success: ok,
                      },
                      response: data,
                    };
                    saveLastRun(ACTION, record);
                    setLastRun(record);
                  } catch (e: any) {
                    setError(e?.message ?? 'Re-enable Always On failed');
                  } finally {
                    setScaling(false);
                    setScaleStartedAt(null);
                  }
                }}
                disabled={scaling || loading}
              >
                <Power size={16} />
                Re-enable Always On
              </PrimaryButton>
            </ButtonRow>
          </Banner>
        )}

        {liveSkuName && !liveSku && (
          <Banner tone="warning" style={{ marginTop: 12 }}>
            Plan SKU is <strong>{liveSkuName}</strong>
            {status?.plan?.sku?.tier ? ` (${status.plan.sku.tier})` : ''}, which is
            outside the set of SKUs this UI knows about. Cost estimates and the
            per-SKU max instance limit will be unavailable until the plan is
            switched to one of: {SCALE_FUNCTION_APP_SKUS.join(', ')}.
          </Banner>
        )}
      </Card>

      <Card>
        <CardTitle>Scale plan</CardTitle>
        <CardDescription>
          Choose a target SKU and worker count for the App Service Plan. A
          nightly heavy run uses <strong>P1v3 ×10</strong>, then guaranteed
          cleanup restores <strong>P0v3 ×1</strong> with Always On enabled.
          Use this form for manual recovery or testing. Per-SKU max instances:
          Basic = 3, Premium v3 = 30.
        </CardDescription>

        <Card style={{ background: '#fafafa' }}>
          <CardTitle style={{ fontSize: 16 }}>Recommended profiles</CardTitle>
          <CardDescription>
            Production measurements peaked at 2.18 GiB working set and about 2
            vCPU per worker. Nightly extraction is latency-bound, so use ten
            smaller workers instead of ten P3v3 workers. Selecting a profile
            fills the form; the normal confirmation step still applies.
          </CardDescription>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
            {(Object.values(FUNCTION_SCALE_PROFILES)).map((profile) => {
              const spec = SCALE_FUNCTION_APP_SKU_SPECS[profile.sku];
              const selected = selectedProfileId === profile.id;
              return (
                <div
                  key={profile.id}
                  style={{
                    border: selected ? '2px solid #2563eb' : '1px solid #d9d9d9',
                    borderRadius: 8,
                    padding: 16,
                    background: selected ? '#eff6ff' : '#fff',
                  }}
                >
                  <div style={{ fontWeight: 700 }}>{profile.label}</div>
                  <div style={{ margin: '6px 0', fontSize: 14 }}>
                    <strong>{profile.sku} × {profile.instances}</strong> ·{' '}
                    {spec.vCpu * profile.instances} vCPU ·{' '}
                    {spec.memoryGb * profile.instances} GB RAM
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 12 }}>
                    {profile.description} Approx.{' '}
                    {fmtUsd(spec.costPerHour * profile.instances)}/hr while active.
                  </div>
                  <SecondaryButton
                    type="button"
                    aria-pressed={selected}
                    onClick={() => selectProfile(profile.id)}
                    disabled={scaling || endpointMissing}
                  >
                    {profile.id === 'idle' ? <ArrowDown size={16} /> : <ArrowUp size={16} />}
                    {selected ? 'Selected' : `Use ${profile.label}`}
                  </SecondaryButton>
                </div>
              );
            })}
          </div>
        </Card>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
          <Field>
            <Label htmlFor="targetSku">Target SKU</Label>
            <Select
              id="targetSku"
              value={targetSku}
              disabled={scaling || endpointMissing}
              onChange={(e) => {
                const next = e.target.value as ScaleFunctionAppSku;
                setTargetSku(next);
                setSkuTouched(true);
                // Clamp instance count down if it now exceeds the new SKU's cap.
                const cap = SCALE_FUNCTION_APP_SKU_SPECS[next].maxCapacity;
                if (target > cap) setTarget(cap);
              }}
              style={{ maxWidth: 220 }}
            >
              {SCALE_FUNCTION_APP_SKUS.map((s) => {
                const spec = SCALE_FUNCTION_APP_SKU_SPECS[s];
                return (
                  <option key={s} value={s}>
                    {s} — {spec.tier} · {spec.vCpu} vCPU · {spec.memoryGb} GB · {fmtUsd(spec.costPerHour)}/hr
                  </option>
                );
              })}
            </Select>
            <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
              {targetSpec.tier} · {targetSpec.vCpu} vCPU · {targetSpec.memoryGb} GB ·
              max <strong>{targetMaxCapacity}</strong> instances
              {liveSku && skuChanged && (
                <>
                  {' · '}
                  Will switch SKU{' '}
                  <strong>{liveSku}</strong> → <strong>{targetSku}</strong>
                </>
              )}
            </div>
          </Field>

          <Field>
            <Label htmlFor="targetInstances">Target instance count</Label>
            <Input
              id="targetInstances"
              type="number"
              min={MIN_INSTANCES}
              max={targetMaxCapacity}
              value={target}
              disabled={scaling || endpointMissing}
              onChange={(e) => {
                setTarget(Number(e.target.value) || MIN_INSTANCES);
                setTargetTouched(true);
              }}
              style={{ maxWidth: 160 }}
            />
            {currentCapacity != null && (
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                Current capacity: <strong>{currentCapacity}</strong>
                {capacityChanged && direction && (
                  <>
                    {' · '}
                    Will scale {direction === 'up' ? 'OUT' : 'IN'}{' '}
                    <strong>{currentCapacity}</strong> → <strong>{target}</strong>
                  </>
                )}
              </div>
            )}
            {overSkuCap && (
              <div style={{ fontSize: 12, color: '#b45309', marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertTriangle size={14} />
                {targetSku} ({targetSpec.tier}) caps at {targetMaxCapacity} workers. ARM will reject this request.
              </div>
            )}
          </Field>
        </div>

        <Field>
          <Label>Always On</Label>
          <InlineLabel>
            <input
              type="checkbox"
              checked={targetAlwaysOn}
              disabled={scaling || endpointMissing}
              onChange={(e) => {
                setTargetAlwaysOn(e.target.checked);
                setAlwaysOnTouched(true);
              }}
            />
            Keep Always On enabled
          </InlineLabel>
          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
            {liveAlwaysOn === undefined
              ? 'Live state unknown — toggle will be applied as-is.'
              : alwaysOnChanged
              ? <>Will switch Always On <strong>{liveAlwaysOn ? 'On' : 'Off'}</strong> → <strong>{targetAlwaysOn ? 'On' : 'Off'}</strong></>
              : <>Currently <strong>{liveAlwaysOn ? 'On' : 'Off'}</strong> · Backend re-applies this on every POST.</>
            }
          </div>
        </Field>

        <Card style={{ marginTop: 16, background: '#fafafa' }}>
          <CardTitle style={{ fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <DollarSign size={16} /> Projected cost
          </CardTitle>
          <CardDescription>
            Indicative — public retail Linux pricing for {targetSku} ×{' '}
            {target} instance{target === 1 ? '' : 's'}. Azure billing is the
            source of truth.
          </CardDescription>
          <StatGrid>
            <Metric
              label="Cost / hour"
              value={fmtUsd(targetHourlyCost)}
              hint={`${fmtUsd(targetSpec.costPerHour)} × ${target}`}
            />
            <Metric
              label="Cost / month"
              value={fmtUsd2(targetMonthlyCost)}
              hint={`${fmtUsd2(targetSpec.costPerMonth)} × ${target} (${HOURS_PER_MONTH} hr/mo)`}
            />
            <Metric
              label="Total vCPU"
              value={targetFleetVCpu}
              hint={`${targetSpec.vCpu} × ${target} workers`}
            />
            <Metric
              label="Total memory"
              value={`${targetFleetMemoryGb} GB`}
              hint={`${targetSpec.memoryGb} GB × ${target} workers`}
            />
            {liveHourlyCost != null && (
              <Metric
                label="Δ vs. current /hr"
                value={
                  (targetHourlyCost - liveHourlyCost >= 0 ? '+' : '') +
                  fmtUsd(targetHourlyCost - liveHourlyCost)
                }
                tone={
                  targetHourlyCost > liveHourlyCost
                    ? 'warning'
                    : targetHourlyCost < liveHourlyCost
                    ? 'success'
                    : 'neutral'
                }
              />
            )}
            {liveMonthlyCost != null && (
              <Metric
                label="Δ vs. current /mo"
                value={
                  (targetMonthlyCost - liveMonthlyCost >= 0 ? '+' : '') +
                  fmtUsd2(targetMonthlyCost - liveMonthlyCost)
                }
                tone={
                  targetMonthlyCost > liveMonthlyCost
                    ? 'warning'
                    : targetMonthlyCost < liveMonthlyCost
                    ? 'success'
                    : 'neutral'
                }
              />
            )}
          </StatGrid>
        </Card>

        <ButtonRow>
          <PrimaryButton
            onClick={handleScale}
            disabled={
              !targetValid || !targetChanged || scaling || endpointMissing || loading
            }
          >
            {direction === 'up' ? <ArrowUp size={16} /> : direction === 'down' ? <ArrowDown size={16} /> : <Zap size={16} />}
            {scaling
              ? 'Scaling…'
              : !targetChanged
              ? 'No change'
              : skuChanged && capacityChanged
              ? `Switch to ${targetSku} @ ${target} instances${alwaysOnChanged ? ` (Always On ${targetAlwaysOn ? 'On' : 'Off'})` : ''}`
              : skuChanged
              ? `Switch SKU to ${targetSku}${alwaysOnChanged ? ` (Always On ${targetAlwaysOn ? 'On' : 'Off'})` : ''}`
              : direction === 'up'
              ? `Scale out to ${target}${alwaysOnChanged ? ` (Always On ${targetAlwaysOn ? 'On' : 'Off'})` : ''}`
              : direction === 'down'
              ? `Scale in to ${target}${alwaysOnChanged ? ` (Always On ${targetAlwaysOn ? 'On' : 'Off'})` : ''}`
              : alwaysOnChanged
              ? `Set Always On ${targetAlwaysOn ? 'On' : 'Off'}`
              : 'Apply'}
          </PrimaryButton>
        </ButtonRow>

        {scaling && scaleStartedAt != null && (
          <RunningIndicator
            startedAt={scaleStartedAt}
            message={`Sending ${
              [
                skuChanged ? `SKU=${targetSku}` : null,
                capacityChanged ? `instances=${target}` : null,
                alwaysOnChanged ? `alwaysOn=${targetAlwaysOn}` : null,
              ].filter(Boolean).join(', ') || 'request'
            } to ARM…`}
          />
        )}
      </Card>

      {scaleResult && (
        <Card>
          <CardTitle>Scale result</CardTitle>
          {scaleResult.result?.success ? (
            <Banner tone="success">
              ARM accepted the scale request.
              {scaleResult.result.previousSku !== scaleResult.result.newSku && (
                <>
                  {' '}SKU:{' '}
                  <strong>{scaleResult.result.previousSku ?? '—'}</strong> →{' '}
                  <strong>{scaleResult.result.newSku ?? '—'}</strong>.
                </>
              )}
              {scaleResult.result.previousCapacity !== scaleResult.result.newCapacity && (
                <>
                  {' '}Capacity:{' '}
                  <strong>{scaleResult.result.previousCapacity}</strong> →{' '}
                  <strong>{scaleResult.result.newCapacity}</strong>.
                </>
              )}
              {scaleResult.result.previousAlwaysOn !== scaleResult.result.newAlwaysOn && (
                <>
                  {' '}Always On:{' '}
                  <strong>{scaleResult.result.previousAlwaysOn ? 'On' : 'Off'}</strong> →{' '}
                  <strong>{scaleResult.result.newAlwaysOn ? 'On' : 'Off'}</strong>.
                </>
              )}
              {' '}Live instance count may take 1–2 minutes to converge.
            </Banner>
          ) : scaleResult.scaleError ? (
            <Banner tone="error">
              ARM rejected the scale request:{' '}
              <strong>{scaleResult.scaleError.message ?? 'Unknown error'}</strong>
              {scaleResult.scaleError.code && <> ({scaleResult.scaleError.code})</>}
            </Banner>
          ) : (
            <Banner tone="warning">
              {scaleResult.message ?? scaleResult.error ?? 'Scale completed with no result.'}
            </Banner>
          )}

          {Array.isArray(scaleResult.warnings) && scaleResult.warnings.length > 0 && (
            <Banner tone="warning" style={{ marginTop: 8 }}>
              {scaleResult.warnings.map((w, i) => (
                <div key={i}>{w}</div>
              ))}
            </Banner>
          )}

          <StatGrid style={{ marginTop: 12 }}>
            <Metric label="Target SKU" value={scaleResult.request?.targetSku ?? '—'} />
            <Metric label="Target instances" value={fmt(scaleResult.request?.targetInstances)} />
            <Metric
              label="Target Always On"
              value={
                (scaleResult.request?.targetAlwaysOn ?? scaleResult.request?.alwaysOn) === true ? 'On' :
                (scaleResult.request?.targetAlwaysOn ?? scaleResult.request?.alwaysOn) === false ? 'Off' : '—'
              }
            />
            <Metric
              label="Previous"
              value={`${scaleResult.result?.previousSku ?? '—'} @ ${fmt(scaleResult.result?.previousCapacity)}${
                typeof scaleResult.result?.previousAlwaysOn === 'boolean'
                  ? ` · AlwaysOn ${scaleResult.result.previousAlwaysOn ? 'On' : 'Off'}`
                  : ''
              }`}
            />
            <Metric
              label="New"
              value={`${scaleResult.result?.newSku ?? '—'} @ ${fmt(scaleResult.result?.newCapacity)}${
                typeof scaleResult.result?.newAlwaysOn === 'boolean'
                  ? ` · AlwaysOn ${scaleResult.result.newAlwaysOn ? 'On' : 'Off'}`
                  : ''
              }`}
              tone={scaleResult.result?.success ? 'success' : 'warning'}
            />
            <Metric label="Live now" value={fmt(scaleResult.liveInstanceCount)} />
          </StatGrid>

          <RawJson data={scaleResult} label="View ARM response" />
        </Card>
      )}

      {Array.isArray(status?.liveInstances) && status!.liveInstances!.length > 0 && (
        <Card>
          <CardTitle>Live workers</CardTitle>
          <CardDescription>
            {status!.liveInstances!.length} worker(s) currently reporting to ARM.
          </CardDescription>
          <div style={{ overflowX: 'auto' }}>
            <SampleTable>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                </tr>
              </thead>
              <tbody>
                {status!.liveInstances!.map((inst, i) => (
                  <tr key={i}>
                    <td><code>{inst.name ?? '—'}</code></td>
                    <td style={{ fontSize: 12, opacity: 0.7 }}>{inst.type ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </SampleTable>
          </div>
        </Card>
      )}

      {status && (
        <Card>
          <CardTitle>Site & plan details</CardTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
            <div>
              <Label>Site</Label>
              <KvList
                items={[
                  ['Name', status.site?.name],
                  ['Default hostname', status.site?.defaultHostName],
                  ['Kind', status.site?.kind],
                  ['Enabled', status.site?.enabled === true ? 'Yes' : status.site?.enabled === false ? 'No' : undefined],
                  ['Linux', status.site?.reserved === true ? 'Yes' : status.site?.reserved === false ? 'No' : undefined],
                  ['HTTPS only', status.site?.httpsOnly === true ? 'Yes' : status.site?.httpsOnly === false ? 'No' : undefined],
                  ['Always On', status.site?.alwaysOn === true ? 'Yes' : status.site?.alwaysOn === false ? 'No' : undefined],
                  ['Image', status.site?.linuxFxVersion],
                  ['Workers (site)', status.site?.numberOfWorkers],
                  ['Last modified (UTC)', status.site?.lastModifiedTimeUtc],
                ]}
              />
            </div>
            <div>
              <Label>App Service Plan</Label>
              <KvList
                items={[
                  ['Name', status.plan?.name],
                  ['Resource group', status.plan?.resourceGroup],
                  ['Status', status.plan?.status],
                  ['Kind', status.plan?.kind],
                  ['SKU', status.plan?.sku?.name],
                  ['Tier', status.plan?.sku?.tier],
                  ['Capacity', status.plan?.capacity],
                  ['Per-site scaling', status.plan?.perSiteScaling === true ? 'Yes' : status.plan?.perSiteScaling === false ? 'No' : undefined],
                  ['Sites on plan', status.plan?.numberOfSites],
                  ['Max elastic workers', status.plan?.maximumElasticWorkerCount ?? undefined],
                ]}
              />
            </div>
          </div>
          <RawJson data={status} label="View raw status JSON" />
        </Card>
      )}

      {infrastructure && (
        <Card>
          <CardTitle>Scalable resource details</CardTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
            <div>
              <Label>Azure SQL</Label>
              <KvList
                items={[
                  ['Server', sql?.serverName],
                  ['Database', sql?.name],
                  ['Edition', sql?.edition],
                  ['SKU', sql?.sku?.name],
                  ['vCores', sql?.vCores],
                  ['Status', sql?.status],
                  ['Service objective', sql?.serviceLevelObjective],
                  ['Error', sql?.error],
                ]}
              />
            </div>
            <div>
              <Label>Reader API</Label>
              <KvList
                items={[
                  ['Name', readerApi?.name],
                  ['Current replicas', readerApi?.currentReplicas],
                  ['Min replicas', readerApi?.minReplicas],
                  ['Max replicas', readerApi?.maxReplicas],
                  ['Running status', readerApi?.runningStatus],
                  ['Provisioning', readerApi?.provisioningState],
                  ['Latest revision', readerApi?.latestReadyRevisionName],
                  ['Error', readerApi?.error],
                ]}
              />
            </div>
            <div>
              <Label>Nightly job runner</Label>
              <KvList
                items={[
                  ['Name', nightlyJob?.name],
                  ['Cron schedule', nightlyJob?.cronSchedule],
                  ['CPU', nightlyJob?.cpu],
                  ['Memory', nightlyJob?.memory],
                  ['Replica timeout', nightlyJob?.replicaTimeoutSeconds == null ? undefined : `${nightlyJob.replicaTimeoutSeconds}s`],
                  ['Retry limit', nightlyJob?.replicaRetryLimit],
                  ['Last execution', nightlyJob?.lastExecution?.status],
                  ['Last start', nightlyJob?.lastExecution?.startTime],
                  ['Last end', nightlyJob?.lastExecution?.endTime],
                  ['Error', nightlyJob?.error],
                ]}
              />
            </div>
            <div>
              <Label>Split WebIQ runner</Label>
              <KvList
                items={[
                  ['Name', webIqJob?.name],
                  ['Cron schedule', webIqJob?.cronSchedule],
                  ['CPU', webIqJob?.cpu],
                  ['Memory', webIqJob?.memory],
                  ['Replica timeout', webIqJob?.replicaTimeoutSeconds == null ? undefined : `${webIqJob.replicaTimeoutSeconds}s`],
                  ['Retry limit', webIqJob?.replicaRetryLimit],
                  ['Last execution', webIqJob?.lastExecution?.status],
                  ['Last start', webIqJob?.lastExecution?.startTime],
                  ['Last end', webIqJob?.lastExecution?.endTime],
                  ['Status source', webIqJob?.statusSource],
                  ['Live ARM status', webIqJob?.armReadError ? 'Unavailable; showing deployed configuration.' : undefined],
                  ['Error', webIqJob?.error],
                ]}
              />
            </div>
          </div>
          <RawJson data={infrastructure} label="View raw infrastructure JSON" />
        </Card>
      )}

      <LastRunCard record={lastRun} />

      <ConfirmDialog
        open={confirmOpen}
        title="Confirm scale request"
        body={
          <>
            {skuChanged && (
              <p>
                You're about to switch the App Service Plan SKU from{' '}
                <strong>{liveSku ?? '—'}</strong> to <strong>{targetSku}</strong>{' '}
                ({targetSpec.tier}).
              </p>
            )}
            {capacityChanged && (
              <p style={{ marginTop: skuChanged ? 12 : 0 }}>
                You're about to change the configured capacity from{' '}
                <strong>{fmt(currentCapacity)}</strong> to <strong>{target}</strong>{' '}
                worker{target === 1 ? '' : 's'}.
              </p>
            )}
            {alwaysOnChanged && (
              <p style={{ marginTop: skuChanged || capacityChanged ? 12 : 0 }}>
                You're about to switch <strong>Always On</strong> from{' '}
                <strong>{liveAlwaysOn ? 'On' : 'Off'}</strong> to{' '}
                <strong>{targetAlwaysOn ? 'On' : 'Off'}</strong>.
              </p>
            )}
            <p style={{ marginTop: 12 }}>
              Projected cost: <strong>{fmtUsd(targetHourlyCost)}/hr</strong> ·{' '}
              <strong>{fmtUsd2(targetMonthlyCost)}/mo</strong>{' '}
              ({fmtUsd(targetSpec.costPerHour)} × {target} instance
              {target === 1 ? '' : 's'}).
              {liveHourlyCost != null && (
                <>
                  {' '}Δ vs. current:{' '}
                  <strong>
                    {(targetHourlyCost - liveHourlyCost >= 0 ? '+' : '') +
                      fmtUsd(targetHourlyCost - liveHourlyCost)}
                    /hr
                  </strong>
                  .
                </>
              )}
            </p>
            <p style={{ marginTop: 12 }}>
              {direction === 'up' && (
                <>Scaling out adds workers; this will increase cost while improving throughput.</>
              )}
              {direction === 'down' && (
                <>Scaling in removes workers; in-flight invocations on removed instances may be interrupted.</>
              )}
              {skuChanged && targetSpec.tier === 'PremiumV3' && (
                <> An Azure Policy may revert PremiumV3 to Basic on its next evaluation.</>
              )}
            </p>
            {overSkuCap && (
              <p style={{ marginTop: 12, color: '#b45309' }}>
                <AlertTriangle size={14} style={{ verticalAlign: 'middle' }} />{' '}
                {targetSku} ({targetSpec.tier}) caps at {targetMaxCapacity}. ARM will
                reject this request.
              </p>
            )}
          </>
        }
        confirmPhrase="SCALE"
        primaryLabel={
          skuChanged && capacityChanged ? `Switch to ${targetSku} @ ${target}` :
          skuChanged ? `Switch SKU to ${targetSku}` :
          direction === 'up' ? `Scale out to ${target}` :
          direction === 'down' ? `Scale in to ${target}` :
          alwaysOnChanged ? `Set Always On ${targetAlwaysOn ? 'On' : 'Off'}` :
          'Apply'
        }
        isProcessing={scaling}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </AdminLayout>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const KvList: React.FC<{ items: Array<[string, React.ReactNode]> }> = ({ items }) => (
  <div style={{ marginTop: 8, fontSize: 13 }}>
    {items
      .filter(([, v]) => v != null && v !== '')
      .map(([k, v], i) => (
        <div
          key={i}
          style={{
            display: 'grid',
            gridTemplateColumns: '160px 1fr',
            gap: 8,
            padding: '4px 0',
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <div style={{ color: '#595959' }}>{k}</div>
          <div style={{ overflowWrap: 'anywhere' }}><code style={{ fontSize: 12 }}>{String(v)}</code></div>
        </div>
      ))}
  </div>
);

// Re-export the icons we don't use directly to keep the import block honest.
// (lucide tree-shakes; this is only to silence "unused import" if a future
// edit drops them.)

export default AdminFunctionsPage;
